import { clampToViewport } from './QuickCreate.js'
import {
  assignLanes,
  freshBooking,
  eventIsPrivateFor,
  currentUser,
  fetchEventDetails
} from '../../functions/calendar/calendarUtils.js'

export const CalWeekView = {
  flow: 'y',
  flex: '1',
  overflow: 'hidden',
  userSelect: 'none',
  display: (el, s) => (s.root.viewMode === 'week' ? 'flex' : 'none'),

  // Banner shown when the week view is scoped to another user (via the
  // profile modal "Open week view" action). Clearing it returns to the
  // current user's own week.
  ViewingAsBanner: {
    display: (el, s) => (s.root.viewOnlyEmail ? 'flex' : 'none'),
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    padding: 'Z A',
    background: 'overlay',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',

    Text: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      text: (el, s) => {
        const email = s.root.viewOnlyEmail
        if (!email) return ''
        const target = el.call('findPerson', email)
        return `Viewing ${target?.name || email}'s week`
      }
    },
    ClearBtn: {
      tag: 'button',
      text: 'Back to my week',
      padding: 'Y Z',
      round: 'Z',
      theme: 'secondary',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      cursor: 'pointer',
      fontSize: 'Y2',
      ':hover': { background: 'overlay' },
      onClick: (e, el, s) => s.root.update({ viewOnlyEmail: null })
    }
  },

  DayHeaders: {
    flow: 'x',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',
    minHeight: 'D',

    TimeGutter: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid',
      userSelect: 'none'
    },

    Days: {
      flex: '1',
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',

      childExtends: 'CalWeekDayHeader',
      childrenAs: 'state',
      children: (el, s) => {
        const root = s.root
        return el.call(
          'getWeekDays',
          root.currentYear,
          root.currentMonth,
          root.currentDay
        )
      }
    }
  },

  ScrollBody: {
    flex: '1',
    overflow: 'auto',
    position: 'relative',
    flow: 'x',
    userSelect: 'none',
    // Don't stretch flex items to viewport height — let TimeGutter and
    // DayColumns expand to their intrinsic 24*hour-row content height so
    // their borders span the full scrollable area.
    alignItems: 'flex-start',

    TimeGutter: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid',
      flow: 'y',
      userSelect: 'none',

      childExtends: 'CalTimeLabel',
      childrenAs: 'state',
      children: (el) =>
        el.call('getHours').map((h) => ({ label: h.label, h: h.h }))
    },

    DayColumns: {
      flex: '1',
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      position: 'relative',

      childExtends: 'CalWeekDayColumn',
      childrenAs: 'state',
      children: (el, s) => {
        const root = s.root
        return el.call(
          'getWeekDays',
          root.currentYear,
          root.currentMonth,
          root.currentDay
        )
      }
    }
  }
}

export const CalWeekDayHeader = {
  flow: 'y',
  align: 'center center',
  padding: 'Z1',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  gap: 'Y2',
  cursor: 'pointer',
  ':hover': { background: 'overlay' },

  // Click the header → open the booking modal pre-set as a whole-day event
  // for that date. Useful for holidays and OOO blocks.
  onClick: (e, el, s) => {
    const date = el.call('formatDate', s.year, s.month, s.day)
    s.root.update({
      showNewBooking: true,
      newBooking: freshBooking({
        date,
        startTime: '00:00',
        endTime: '23:59',
        allDay: true,
        type: 'busy'
      })
    })
  },

  DayName: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '500',
    color: 'caption',
    text: (el, s) => s.dayName || ''
  },

  DayNum: {
    tag: 'span',
    width: 'B1',
    height: 'B1',
    round: '50%',
    align: 'center center',
    fontSize: 'B',
    fontWeight: '400',
    background: (el, s) =>
      el.call('isToday', s.year, s.month, s.day) ? 'blue' : 'transparent',
    color: (el, s) =>
      el.call('isToday', s.year, s.month, s.day) ? 'white' : 'title',
    text: (el, s) => String(s.day)
  }
}

export const CalTimeLabel = {
  height: '56px',
  minHeight: '56px',
  padding: 'Y 0 0 Z',
  fontSize: 'Y2',
  color: 'caption',
  align: 'flex-start right',
  paddingRight: 'Z1',
  text: (el, s) => (s.h === 0 ? '' : s.label)
}

export const CalWeekDayColumn = {
  position: 'relative',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  cursor: 'cell',
  attr: {
    'data-cal-week-col-date': (el, s) =>
      el.call('formatDate', s.year, s.month, s.day)
  },

  // Click / double-click / drag to create. Drag also paints a live preview
  // (CalDragPreview) that follows the cursor in real time.
  onDblClick: (e, el, s) => {
    if (e.target.closest('button')) return
    const rect = el.node.getBoundingClientRect()
    const offsetY = e.clientY - rect.top
    const startTime = el.call('pxToTime', offsetY)
    const endTime = el.call('addMinutes', startTime, 60)
    const root = s.root
    root.update({
      showNewBooking: true,
      newBooking: freshBooking({
        date: el.call('formatDate', s.year, s.month, s.day),
        startTime,
        endTime,
        type: 'meeting'
      })
    })
  },
  onMousedown: (e, el, s) => {
    if (e.target.closest('button')) return
    const rect = el.node.getBoundingClientRect()
    el._dragStartY = e.clientY - rect.top
    el._dragMoved = false
    const startTime = el.call('pxToTime', el._dragStartY)
    s.root.update({
      dragPreview: {
        date: el.call('formatDate', s.year, s.month, s.day),
        startTime,
        endTime: el.call('addMinutes', startTime, 30),
        color: s.root.newBooking?.color || 'blue'
      }
    })
  },
  onMousemove: (e, el, s) => {
    if (el._dragStartY == null) return
    const rect = el.node.getBoundingClientRect()
    const curY = e.clientY - rect.top
    if (Math.abs(curY - el._dragStartY) > 5) el._dragMoved = true
    const y0 = Math.min(el._dragStartY, curY)
    const y1 = Math.max(el._dragStartY, curY)
    const startTime = el.call('pxToTime', y0)
    const endTime = el.call('pxToTime', y1)
    const finalEnd =
      endTime > startTime ? endTime : el.call('addMinutes', startTime, 30)
    s.root.update({
      dragPreview: {
        date: el.call('formatDate', s.year, s.month, s.day),
        startTime,
        endTime: finalEnd,
        color: s.root.newBooking?.color || 'blue'
      }
    })
  },
  onMouseup: (e, el, s) => {
    if (el._dragStartY == null) return
    const rect = el.node.getBoundingClientRect()
    const startY = el._dragStartY
    const endY = e.clientY - rect.top
    const dragMoved = el._dragMoved
    el._dragStartY = null
    el._dragMoved = false
    // A plain click (no drag) should NOT create a slot — only drag-out
    // selections open the quick-create overlay.
    if (!dragMoved) return

    const y0 = Math.min(startY, endY)
    const y1 = Math.max(startY, endY)
    const startTime = el.call('pxToTime', y0)
    const endTime = el.call('pxToTime', y1)

    const root = s.root
    // Keep the dragPreview alive as a pending slot the user can still drag
    // around while the QuickCreate popover is showing.
    const dateStr = el.call('formatDate', s.year, s.month, s.day)
    const colRect = el.node.getBoundingClientRect()
    // Anchor the popover at the TOP of the slot (not the cursor) so it
    // visually aligns with the start of the dragged-out time block.
    const slotTop = colRect.top + el.call('eventToTop', startTime)
    const pos = clampToViewport(colRect.right + 8, slotTop)
    root.update({
      dragPreview: {
        date: dateStr,
        startTime,
        endTime,
        color: 'green',
        pending: true
      },
      quickCreate: {
        date: dateStr,
        startTime,
        endTime,
        memberEmails: [],
        title: '',
        x: pos.x,
        y: pos.y
      }
    })
  },
  onMouseleave: (e, el, s) => {
    if (el._dragStartY != null) {
      el._dragStartY = null
      el._dragMoved = false
      s.root.update({ dragPreview: null })
    }
  },

  // HourLines flows naturally (not absolute) so the column has intrinsic
  // height = 24 * hour-row-height. Without this the column collapses to the
  // visible viewport and the right border ends mid-scroll.
  HourLines: {
    flow: 'y',
    pointerEvents: 'none',

    childExtends: 'CalHourLine',
    childrenAs: 'state',
    children: (el) => el.call('getHours').map((h, i) => ({ h: i }))
  },

  // Show the CURRENT user's own unavailable bands (off-hours + busy/ooo
  // events from events array), tz-aware, on every visible day.
  Unavailable: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',

    childExtends: 'CalUnavailableSlot',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s.root
      if (!root) return []
      void root.currentMinutes
      const me = currentUser(root)
      if (!me) return []
      const baseMs = new Date(s.year, s.month, s.day, 0, 0).getTime()
      const myEvents = (root.events || []).filter(
        (ev) =>
          ev.googleCalendarId === root.currentUserEmail ||
          (!ev.googleCalendarId && ev.creatorEmail === root.currentUserEmail)
      )
      return el.call('getUnavailableRanges', me, baseMs, 24 * 60, 15, myEvents)
    }
  },

  Events: {
    position: 'absolute',
    inset: '0',
    padding: '0 2px',
    pointerEvents: 'none', // child events re-enable

    childExtends: 'CalWeekEvent',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s.root
      if (!root) return []
      // Only meetings render as event chips. Busy/ooo show via the
      // Unavailable layer above as red bands. `viewOnlyEmail` is set when the
      // user opens "Open week view" from a person profile modal.
      const me = root.viewOnlyEmail || root.currentUserEmail
      const mine = (root.events || []).filter(
        (ev) =>
          ev.googleCalendarId === me ||
          (!ev.googleCalendarId && ev.creatorEmail === me)
      )
      const dayEvents = el
        .call('getEventsForDay', mine, s.year, s.month, s.day)
        .filter((ev) => !ev.type || ev.type === 'meeting' || ev.type === 'busy' || ev.type === 'general')
      return assignLanes(dayEvents)
    }
  },

  CalDragPreview: {},
  CalDragMoveGhost: {},

  // "Now" indicator — only renders in today's column. Line spans the column
  // width; the dot is centered on the line at the column's left edge.
  // NOTE: child keys are NowBar/NowDot (not Line/Dot) to avoid implicit
  // extension of brand's `Line` component which has its own margin/height.
  NowIndicator: {
    display: (el, s) =>
      el.call('isToday', s.year, s.month, s.day) ? 'block' : 'none',
    position: 'absolute',
    left: '0',
    right: '0',
    height: '0',
    zIndex: 5,
    pointerEvents: 'none',
    top: (el, s) => `${((s?.root?.currentMinutes || 0) / 60) * 56}px`,

    NowBar: {
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      height: '2px',
      transform: 'translateY(-50%)',
      background: 'red'
    },
    NowDot: {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '12px',
      height: '12px',
      transform: 'translate(-50%, -50%)',
      round: '50%',
      background: 'red'
    }
  }
}

// Cross-day drag ghost. Renders only in the column whose date matches the
// current dragOverlay, mirroring the dragged event's new position. This lets
// horizontal drags appear in the target column without re-rendering the
// canonical events array on every frame.
export const CalDragOverlayGhost = {
  display: (el, s) => {
    const ov = s?.root?.dragOverlay
    if (!ov) return 'none'
    const colDate = el.call('formatDate', s.year, s.month, s.day)
    return ov.date === colDate ? 'flex' : 'none'
  },
  position: 'absolute',
  left: '2px',
  right: '2px',
  round: 'Z',
  padding: 'Y2 Z',
  zIndex: 3,
  flow: 'y',
  color: 'white',
  fontSize: 'Y2',
  fontWeight: '500',
  opacity: '.85',
  borderWidth: '2px',
  borderStyle: 'dashed',
  borderColor: 'white.6',
  pointerEvents: 'none',
  background: 'green',
  top: (el, s) =>
    `${el.call('eventToTop', s?.root?.dragOverlay?.startTime || '00:00')}px`,
  height: (el, s) =>
    `${el.call('eventToHeight', s?.root?.dragOverlay?.startTime || '00:00', s?.root?.dragOverlay?.endTime || '00:30')}px`,
  Time: {
    tag: 'span',
    text: (el, s) =>
      `${s?.root?.dragOverlay?.startTime || ''} – ${s?.root?.dragOverlay?.endTime || ''}`
  }
}

// Live drag ghost for an existing event being moved/resized. Renders only
// in the column whose date matches `dragMove.date`. Position comes entirely
// from reactive state — no DOM mutation.
export const CalDragMoveGhost = {
  display: (el, s) => {
    const dm = s?.root?.dragMove
    if (!dm) return 'none'
    return dm.date === el.call('formatDate', s.year, s.month, s.day)
      ? 'flex'
      : 'none'
  },
  position: 'absolute',
  left: '2px',
  right: '2px',
  round: 'Z',
  padding: 'Y2 Z',
  zIndex: 6,
  flow: 'y',
  color: 'white',
  fontSize: 'Y2',
  fontWeight: '500',
  opacity: '.85',
  borderWidth: '2px',
  borderStyle: 'dashed',
  borderColor: 'white.6',
  pointerEvents: 'none',
  background: 'green',
  top: (el, s) =>
    `${el.call('eventToTop', s?.root?.dragMove?.startTime || '00:00')}px`,
  height: (el, s) =>
    `${el.call('eventToHeight', s?.root?.dragMove?.startTime || '00:00', s?.root?.dragMove?.endTime || '00:30')}px`,
  Time: {
    tag: 'span',
    text: (el, s) =>
      `${s?.root?.dragMove?.startTime || ''} – ${s?.root?.dragMove?.endTime || ''}`
  }
}

export const CalHourLine = {
  height: '56px',
  minHeight: '56px',
  borderBottomWidth: '1px',
  borderBottomColor: 'line',
  borderBottomStyle: 'solid'
}

// Drag preview slot. Two phases:
//  1. Active drag (dragPreview.pending !== true): cursor follows, no inputs.
//  2. Pending (dragPreview.pending === true): the slot stays put after mouseup
//     while the QuickCreate popover is open. The user can grab it and drag
//     vertically (re-time) or horizontally (cross-day) without losing the
//     popover. On save/cancel, dragPreview is cleared.
export const CalDragPreview = {
  display: (el, s) => {
    const dp = s?.root?.dragPreview
    if (!dp) return 'none'
    return dp.date === el.call('formatDate', s.year, s.month, s.day)
      ? 'flex'
      : 'none'
  },
  position: 'absolute',
  left: '2px',
  right: '2px',
  round: 'Z',
  padding: 'Y2 Z',
  zIndex: 4,
  flow: 'y',
  color: 'white',
  fontSize: 'Y2',
  fontWeight: '500',
  opacity: '.85',
  borderWidth: '2px',
  borderStyle: 'dashed',
  borderColor: 'white.5',
  // Slot color reflects the currently-selected event type — meeting=green, general=blue, busy=red.
  background: (el, s) => {
    const t =
      s?.root?.quickCreate?.type || s?.root?.newBooking?.type || 'meeting'
    if (t === 'general') return 'blue'
    if (t === 'busy') return 'red'
    return 'green'
  },
  top: (el, s) =>
    `${el.call('eventToTop', s?.root?.dragPreview?.startTime || '00:00')}px`,
  height: (el, s) =>
    `${el.call('eventToHeight', s?.root?.dragPreview?.startTime || '00:00', s?.root?.dragPreview?.endTime || '00:30')}px`,
  // Pending mode is interactive (grab to move). Active drag is non-interactive.
  pointerEvents: (el, s) => (s?.root?.dragPreview?.pending ? 'auto' : 'none'),
  cursor: 'grab',

  onMousedown: (e, el, s) => {
    const root = s?.root
    const dp = root?.dragPreview
    if (!dp?.pending) return
    e.stopPropagation()
    root.update({
      activePendingMove: {
        startX: e.clientX,
        startY: e.clientY,
        origDate: dp.date,
        origStart: dp.startTime,
        origEnd: dp.endTime
      }
    })
  },

  Time: {
    tag: 'span',
    text: (el, s) =>
      `${s?.root?.dragPreview?.startTime || ''} – ${s?.root?.dragPreview?.endTime || ''}`,
    opacity: '.95'
  }
}

export const CalWeekEvent = {
  tag: 'div',
  position: 'absolute',
  // Cascaded stack layout for overlapping events. Each successive lane is
  // offset horizontally, and later lanes extend to the column's right edge
  // so they sit on top of prior ones while still leaving a clickable sliver
  // of each earlier event exposed on the left.
  left: (el, s) => {
    const lanes = s._lanes || 1
    const lane = s._lane || 0
    if (lanes <= 1) return '2px'
    return `calc(${(lane / lanes) * 100}% + 2px)`
  },
  width: (el, s) => {
    const lanes = s._lanes || 1
    const lane = s._lane || 0
    if (lanes <= 1) return 'calc(100% - 4px)'
    // Extend every stacked card to the column's right edge; the higher lane
    // sits on top (zIndex below) and earlier cards peek out from behind.
    return `calc(${(1 - lane / lanes) * 100}% - 4px)`
  },
  zIndex: (el, s) => {
    // Later lanes sit on top so the stack cascades front-to-back naturally.
    // Hover / focus bumps above the rest via the existing :hover rule.
    return 2 + (s._lane || 0)
  },
  // Stacked-event left separator. v3.14 DOMQL doesn't honor reactive
  // border* longhands here, so we draw the slit with a `boxShadow` inset
  // which always paints. Dark translucent for contrast against any of the
  // event background colors (green / blue / red).
  boxShadow: (el, s) =>
    (s._lanes || 1) > 1 && (s._lane || 0) > 0
      ? 'inset 2px 0 0 var(--color-shadow)'
      : 'none',
  round: 'Z',
  padding: 'Y2 Z',
  cursor: 'grab',
  overflow: 'hidden',
  color: 'white',
  fontSize: 'Y2',
  fontWeight: '500',
  flow: 'y',
  align: 'flex-start flex-start',
  pointerEvents: 'auto',
  userSelect: 'none',
  // 3-type color scheme:
  //   - meeting (default)        → green (booked, ad-hoc)
  //   - general (company-wide)   → blue
  //   - busy/ooo                 → never reach this container (filtered out)
  background: (el, s) => {
    // Private events (superadmin meetings, external creators) render as a
    // plain green "Meeting" chip for non-creators — no detail leak.
    if (eventIsPrivateFor(s, currentUser(s?.root))) return 'green'
    if (s.type === 'general') return 'blue'
    if (s.type === 'busy') return 'red'
    return 'green'
  },
  // Proposal events (a participant has suggested a new time/day) render with
  // reduced opacity + dashed border until the creator confirms. Live drag
  // also dims the source while a ghost shows the new position.
  opacity: (el, s) => {
    const dm = s?.root?.dragMove
    if (dm && dm.id === s.id) return '.35'
    return s.isProposal ? '.55' : '1'
  },
  borderWidth: (el, s) => (s.isProposal ? '2px' : '0'),
  borderStyle: (el, s) => (s.isProposal ? 'dashed' : 'none'),
  borderColor: 'white.6',
  // Position is reactive against the canonical event state. During a drag
  // the move handler writes directly to this node's inline style.top/height
  // (bypassing reactivity) for smooth per-frame motion, then commits the
  // final position to root.events on mouseup so this getter takes over.
  top: (el, s) => `${el.call('eventToTop', s.startTime)}px`,
  height: (el, s) => `${el.call('eventToHeight', s.startTime, s.endTime)}px`,
  attr: {
    'data-cal-event-id': (el, s) => String(s.id || '')
  },
  ':hover': { opacity: '.95', zIndex: 1 },
  ':active': { cursor: 'grabbing' },

  // Drag the body to move. Resize handles below stop propagation to prevent
  // this from also kicking in. Distinguish click vs drag with a 5px threshold.
  onMousedown: (e, el, s) => {
    e.stopPropagation()
    el._downY = e.clientY
    el._moved = false
    el.state.root.update({
      activeMove: {
        id: s.id,
        mode: 'move',
        startY: e.clientY,
        origStart: s.startTime,
        origEnd: s.endTime,
        origDate: s.date
      }
    })
  },
  // Lock the team-view hover preview to this event's start time while the
  // cursor is over the event. stopPropagation prevents the parent column /
  // ScrollBody mousemove from overwriting hoverTime with the cursor's Y.
  onMouseenter: (e, el, s) => {
    el.state.root.update({ hoverTime: s.startTime })
  },
  onMousemove: (e, el) => {
    e.stopPropagation()
    if (el._downY != null && Math.abs(e.clientY - el._downY) > 5)
      el._moved = true
  },
  onMouseleave: (e, el, s) => {
    // Clear so the next ScrollBody mousemove can resume cursor-tracking.
    if (el.state.root.hoverTime != null) {
      el.state.root.update({ hoverTime: null })
    }
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (el._moved) {
      el._moved = false
      el._downY = null
      return
    }
    el._downY = null
    const root = s.root
    root.update({ selectedEvent: s, showEventDetail: true })
    if (s.invitees === undefined || s.description === undefined)
      fetchEventDetails(s.id, root)
  },

  Title: {
    tag: 'span',
    text: (el, s) => {
      if (eventIsPrivateFor(s, currentUser(s.root))) return 'Meeting'
      return s.title || ''
    },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    lineHeight: '1.2',
    flexShrink: '0'
  },
  // Hide the time row on slot-sized events (≤30 min) so the title gets the
  // limited vertical space instead of being clipped behind the time string.
  Time: {
    tag: 'span',
    text: (el, s) => `${s.startTime} – ${s.endTime}`,
    opacity: '.8',
    display: (el, s) => {
      const [sh, sm] = (s.startTime || '00:00').split(':').map(Number)
      const [eh, em] = (s.endTime || '00:00').split(':').map(Number)
      let mins = eh * 60 + em - (sh * 60 + sm)
      if (mins <= 0) mins += 24 * 60
      return mins <= 30 ? 'none' : 'block'
    }
  },

  // Top resize handle — drag to change start time
  ResizeTop: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '6px',
    cursor: 'ns-resize',
    background: 'transparent',
    onMousedown: (e, el, s) => {
      e.stopPropagation()
      const ev = el.parent
      ev._moved = true // prevent click-as-open after resize
      el.state.root.update({
        activeMove: {
          id: s.id,
          mode: 'resize-top',
          startY: e.clientY,
          origStart: s.startTime,
          origEnd: s.endTime,
          origDate: s.date
        }
      })
    }
  },

  // Bottom resize handle — drag to change end time
  ResizeBottom: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '6px',
    cursor: 'ns-resize',
    background: 'transparent',
    onMousedown: (e, el, s) => {
      e.stopPropagation()
      const ev = el.parent
      ev._moved = true
      el.state.root.update({
        activeMove: {
          id: s.id,
          mode: 'resize-bottom',
          startY: e.clientY,
          origStart: s.startTime,
          origEnd: s.endTime,
          origDate: s.date
        }
      })
    }
  }
}
